import {AgentUpsertResult, Company, UpsertableAgent, upsertAgentVerb, Venue} from "../../../rpc/webrpc-client.gen.ts";
import {createFileRoute} from "@tanstack/react-router";
import {BulkUploader} from "../../../upload/bulk-upload.tsx";
import {useTranslation} from "react-i18next";
import React, {JSX} from "react";
import {AgentCapsule} from "../../../agent/capsule.tsx";
import {CompanyCapsule} from "../../../company/capsule.tsx";
import {VenueCapsule} from "../../../venue/capsule.tsx";
import {Box, Sheet, Stack} from "@mui/joy";
import {PageBody, PageHeader, PageShell} from "../../../routing/shell.tsx";
import {TopBar} from "../../../components/TopBar.tsx";
import {isEmpty} from "lodash";
import {useMothership} from "../../../tq/tqx.tsx";

interface CsvAgentFood {
    companyId: string;
    code: string;
    displayName: string;
    sortName?: string;
    telno?: string;
    email?: string;
    canAdmin?: boolean | string;
    canDrive?: boolean | string;
    canDispatch?: boolean | string;
    admitterFor?: string;
    inspectorFor?: string;
    supervisorFor?: string;
    loginName?: string;
    password?: string;
    companyCode: Company["code"];
    companyName: Company["displayName"];
    admitterForCode: Venue["code"];
    admitterForName: Venue["displayName"];
    inspectorForCode: Venue["code"];
    inspectorForName: Venue["displayName"];
    supervisorForCode: Venue["code"];
    supervisorForName: Venue["displayName"];
}

function csvAgentFoodToEnsurableAgent(csvfood: CsvAgentFood): UpsertableAgent {
    const rv: UpsertableAgent = {
        companyId: csvfood.companyId,
        companyCode: csvfood.companyCode,
        companyName: csvfood.companyName,
        code: csvfood.code,
        displayName: csvfood.displayName,
        sortName: csvfood.sortName,
        telno: csvfood.telno,
        email: csvfood.email,
    };
    if (csvfood.canAdmin) {
        rv.canAdmin = true;
    }
    if (csvfood.canDispatch) {
        rv.canDispatch = true;
    }
    if (csvfood.canDrive) {
        rv.canDrive = true;
    }
    if (csvfood.admitterFor) {
        rv.admitterFor = csvfood.admitterFor;
    }
    if (csvfood.admitterForCode) {
        rv.admitterForCode = csvfood.admitterForCode;
    }
    if (csvfood.admitterForName) {
        rv.admitterForName = csvfood.admitterForName;
    }
    if (csvfood.inspectorFor) {
        rv.inspectorFor = csvfood.inspectorFor;
    }
    if (csvfood.inspectorForCode) {
        rv.inspectorForCode = csvfood.inspectorForCode;
    }
    if (csvfood.inspectorForName) {
        rv.inspectorForName = csvfood.inspectorForName;
    }
    if (csvfood.supervisorFor) {
        rv.supervisorFor = csvfood.supervisorFor;
    }
    if (csvfood.supervisorForCode) {
        rv.supervisorForCode = csvfood.supervisorForCode;
    }
    if (csvfood.supervisorForName) {
        rv.supervisorForName = csvfood.supervisorForName;
    }
    if (csvfood.loginName) {
        rv.loginName = csvfood.loginName;
    }
    if (csvfood.password) {
        rv.password = csvfood.password;
    }

    return rv;
}

function AdminAgent(): JSX.Element {
    const {t} = useTranslation();
    const mama = useMothership();
    return <PageShell>
        <PageHeader><TopBar/></PageHeader>
        <PageBody>
            <BulkUploader<CsvAgentFood, UpsertableAgent, AgentUpsertResult>
                entityTypeLabel={t("entity.agent")}
                csvToUpsertFood={csvAgentFoodToEnsurableAgent}
                upsertTqo={{
                    mutationKey: [upsertAgentVerb],
                    mutationFn: (args) => mama.upsertAgent(args)
                        .then(tqrv => tqrv.result),
                }}
                upsertFoodCapsule={(food: UpsertableAgent) =>
                    <AgentCapsule agent={{displayName: food.displayName, code: food.code}}/>
                }
                existingId={(result) => result.existingId ?? null}
                newId={(result) => result.newId ?? null}
                completedCapsule={({food, color, variant}) => {
                    return <Sheet color={color} variant={variant}>
                        <AgentCapsule agent={{
                            displayName: food.displayName,
                            code: food.code
                        }}/>
                    </Sheet>
                }}
                conflictCapsules={(tqr: AgentUpsertResult) => {
                    const rv: React.JSX.Element[] = [];
                    if (tqr.conflict) {
                        rv.push(<AgentCapsule
                            key="agent"
                            agent={{
                                displayName: tqr.conflict.displayName,
                                code: tqr.conflict.code
                            }}/>);
                    }
                    if (tqr.companyConflict) {
                        rv.push(<Stack direction="row">
                            <Box>{t("conflict.requested")}</Box>
                            <CompanyCapsule key="company"
                                            company={{
                                                displayName: tqr.companyConflict.requested.displayName,
                                                code: tqr.companyConflict.requested.code
                                            }}/>
                            <Box>{t("conflict.existing")}</Box>
                            <CompanyCapsule key="company"
                                            company={{
                                                displayName: tqr.companyConflict.existing.displayName,
                                                code: tqr.companyConflict.existing.code
                                            }}/>
                        </Stack>);
                    }
                    if (tqr.venueConflict) {
                        rv.push(<Stack direction="row">
                            <Box>{t("conflict.requested")}</Box>
                            <VenueCapsule key="venue"
                                          venue={{
                                              displayName: tqr.venueConflict.requested.displayName,
                                              code: tqr.venueConflict.existing.code
                                          }}/>
                            <Box>{t("conflict.existing")}</Box>
                            <VenueCapsule key="venue"
                                          venue={{
                                              displayName: tqr.venueConflict.existing.displayName,
                                              code: tqr.venueConflict.existing.code
                                          }}/>
                        </Stack>);
                    }
                    return isEmpty(rv) ? null : rv;
                }}/>
        </PageBody>
    </PageShell>;
}

export const Route = createFileRoute("/_auth/admin/agent")({
    component: AdminAgent,
});


